import React, { useState } from 'react';
import {
  Paper,
  Stack,
  Group,
  Box,
  TextInput,
  Select,
  Button,
  Text,
  SegmentedControl,
  Badge,
  Divider
} from '@mantine/core';
import {
  IconSearch,
  IconSortAscending,
  IconSortDescending,
  IconFilter,
  IconFilterOff,
  IconCategory,
  IconMapPin,
  IconMapPinFilled
} from '@tabler/icons-react';
import { PaginatedResult } from '../../../../domain/common/PaginatedResult';
import { RegionListItemDto } from '../../../../domain/entities/Region';
import { SubAreaDto } from '../../../../domain/entities/SubArea';

// SESSION_320: Sort options for businesses list
const SORT_OPTIONS = [
  { value: 'Name', label: 'Name' },
  { value: 'MentionCount', label: 'Most Mentions' },
  { value: 'Category', label: 'Category' },
  { value: 'RegionName', label: 'Region' },
  { value: 'CreatedAt', label: 'Date Created' }
];

interface BusinessesFiltersProps {
  // Search
  onSearch: (searchTerm: string) => void;

  // Filter data
  categories?: string[];
  regions?: PaginatedResult<RegionListItemDto>;
  subAreas?: SubAreaDto[];

  // Filter state
  selectedCategory: string | null;
  onCategoryChange: (category: string | null) => void;

  selectedRegionId: string | null;
  onRegionChange: (regionId: string | null) => void;

  selectedRegionStringId: string | null;

  selectedSubAreaId: string | null;
  onSubAreaChange: (subAreaId: string | null) => void;

  // Sprint 045: Active/Inactive filter
  selectedIsActive: boolean | undefined;
  onIsActiveChange: (isActive: boolean | undefined) => void;

  // SESSION_320: Sort state
  sortBy: string;
  sortDescending: boolean;
  onSortChange: (sortBy: string, sortDescending: boolean) => void;

  // Page size
  pageSize: string;
  onPageSizeChange: (size: string | null) => void;

  // Clear filters
  hasActiveFilters: boolean;
  onClearFilters: () => void;

  // Display info
  displayRange: string;
  isFetching: boolean;
}

/**
 * BusinessesFilters - Filter controls for businesses page
 * SESSION_321: Redesigned with improved visual hierarchy and grouping
 *
 * Layout Zones:
 * 1. Search Zone - Primary action, prominent placement
 * 2. Filter Zone - Secondary, grouped with subtle background
 * 3. Control Bar - Tertiary, sort/pagination with results count
 *
 * Features:
 * - Search by business name (with button trigger, no debouncing)
 * - Filter by Category (immediate trigger)
 * - Filter by Region (immediate trigger)
 * - Filter by SubArea (immediate trigger, depends on Region)
 * - Sort with unified direction control
 * - Page size selector
 * - Clear all filters button
 */
export const BusinessesFilters: React.FC<BusinessesFiltersProps> = ({
  onSearch,
  categories,
  regions,
  subAreas,
  selectedCategory,
  onCategoryChange,
  selectedRegionId,
  onRegionChange,
  selectedRegionStringId,
  selectedSubAreaId,
  onSubAreaChange,
  selectedIsActive,
  onIsActiveChange,
  sortBy,
  sortDescending,
  onSortChange,
  pageSize,
  onPageSizeChange,
  hasActiveFilters,
  onClearFilters,
  displayRange,
  isFetching
}): React.ReactElement => {
  // Local state for search input (not yet submitted)
  const [searchInput, setSearchInput] = useState<string>('');

  /**
   * Handle search submission (button click or Enter key)
   */
  const handleSearchSubmit = (): void => {
    onSearch(searchInput.trim());
  };

  /**
   * Handle Enter key press in search input
   */
  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  /**
   * Count active filters for badge display
   */
  const activeFilterCount = [
    selectedCategory,
    selectedRegionId,
    selectedSubAreaId,
    selectedIsActive !== undefined ? 'active' : null
  ].filter(Boolean).length;

  return (
    <Paper shadow="sm" p={0} radius="md" withBorder>
      <Stack gap={0}>
        {/* ═══════════════════════════════════════════════════════════════
            ZONE 1: SEARCH - Primary Action
            Full width, prominent styling to emphasize as main entry point
        ═══════════════════════════════════════════════════════════════ */}
        <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
          <Group gap="sm" align="flex-end">
            <TextInput
              placeholder="Search businesses by name..."
              leftSection={<IconSearch size={18} stroke={1.5} />}
              value={searchInput}
              onChange={(event): void => setSearchInput(event.currentTarget.value)}
              onKeyDown={handleSearchKeyDown}
              size="md"
              style={{ flex: 1, minWidth: 200 }}
              styles={{
                input: {
                  '&:focus': {
                    borderColor: 'var(--mantine-color-brandPrimary-5)'
                  }
                }
              }}
            />
            <Button
              variant="filled"
              color="brandPrimary"
              onClick={handleSearchSubmit}
              size="md"
              leftSection={<IconSearch size={18} />}
            >
              Search
            </Button>
          </Group>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            ZONE 2: FILTERS - Secondary Controls
            Grouped visually with subtle background, icon labels for clarity
        ═══════════════════════════════════════════════════════════════ */}
        <Box
          p="md"
          style={{
            backgroundColor: 'var(--mantine-color-gray-0)',
            borderBottom: '1px solid var(--mantine-color-gray-2)'
          }}
        >
          <Group gap="xs" mb="sm" align="center">
            <IconFilter size={16} stroke={1.5} style={{ color: 'var(--mantine-color-gray-6)' }} />
            <Text size="sm" fw={500} c="dimmed">Filters</Text>
            {activeFilterCount > 0 && (
              <Badge size="sm" variant="filled" color="brandPrimary" circle>
                {activeFilterCount}
              </Badge>
            )}
            {hasActiveFilters && (
              <Button
                variant="subtle"
                color="gray"
                onClick={onClearFilters}
                size="xs"
                leftSection={<IconFilterOff size={14} />}
                ml="auto"
              >
                Clear All
              </Button>
            )}
          </Group>

          <Group gap="md" wrap="wrap">
            <Select
              placeholder="All Categories"
              leftSection={<IconCategory size={16} stroke={1.5} />}
              value={selectedCategory}
              onChange={onCategoryChange}
              data={categories?.map(c => ({ value: c, label: c })) ?? []}
              clearable
              searchable
              size="sm"
              style={{ width: 180 }}
              styles={{
                input: { paddingLeft: 36 }
              }}
            />
            <Select
              placeholder="All Regions"
              leftSection={<IconMapPin size={16} stroke={1.5} />}
              value={selectedRegionId}
              onChange={onRegionChange}
              data={regions?.items?.map(r => ({ value: r.id, label: r.name })) ?? []}
              clearable
              searchable
              size="sm"
              style={{ width: 180 }}
              styles={{
                input: { paddingLeft: 36 }
              }}
            />
            <Select
              placeholder={selectedRegionStringId ? "All SubAreas" : "Select Region"}
              leftSection={<IconMapPinFilled size={16} stroke={1.5} />}
              value={selectedSubAreaId}
              onChange={onSubAreaChange}
              data={subAreas?.map(sa => ({ value: sa.id, label: sa.name })) ?? []}
              disabled={!selectedRegionStringId}
              clearable
              searchable
              size="sm"
              style={{ width: 180 }}
              styles={{
                input: {
                  paddingLeft: 36,
                  '&:disabled': {
                    backgroundColor: 'var(--mantine-color-gray-1)',
                    color: 'var(--mantine-color-gray-5)'
                  }
                }
              }}
            />
            <SegmentedControl
              value={selectedIsActive === undefined ? 'all' : selectedIsActive ? 'active' : 'inactive'}
              onChange={(value): void => {
                if (value === 'all') {
                  onIsActiveChange(undefined);
                } else {
                  onIsActiveChange(value === 'active');
                }
              }}
              data={[
                { label: 'All', value: 'all' },
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' }
              ]}
              size="sm"
            />
          </Group>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            ZONE 3: CONTROL BAR - Tertiary (Sort, Pagination, Results)
            Compact bar with results on left, controls on right
        ═══════════════════════════════════════════════════════════════ */}
        <Group p="sm" px="md" justify="space-between" wrap="wrap" gap="md">
          {/* Results Count - Left Side */}
          <Text size="sm" c="dimmed" fw={500}>
            {displayRange}
            {isFetching && (
              <Text component="span" c="blue" size="sm" ml="xs">
                updating...
              </Text>
            )}
          </Text>

          {/* Sort & Pagination - Right Side */}
          <Group gap="md" wrap="wrap">
            {/* Sort Controls - Unified Group */}
            <Group gap="xs" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 'var(--mantine-radius-sm)', padding: '4px 8px' }}>
              <Text size="xs" c="dimmed" fw={500}>Sort:</Text>
              <Select
                value={sortBy}
                onChange={(value): void => onSortChange(value || 'Name', sortDescending)}
                data={SORT_OPTIONS}
                size="xs"
                style={{ width: 130 }}
                styles={{
                  input: {
                    fontWeight: 500,
                    minHeight: 28
                  }
                }}
              />
              <SegmentedControl
                value={sortDescending ? 'desc' : 'asc'}
                onChange={(value): void => onSortChange(sortBy, value === 'desc')}
                size="xs"
                data={[
                  {
                    value: 'asc',
                    label: <IconSortAscending size={14} stroke={2} />
                  },
                  {
                    value: 'desc',
                    label: <IconSortDescending size={14} stroke={2} />
                  }
                ]}
                styles={{
                  root: { backgroundColor: 'var(--mantine-color-gray-1)' }
                }}
              />
            </Group>

            <Divider orientation="vertical" />

            {/* Page Size */}
            <Group gap="xs">
              <Text size="xs" c="dimmed" fw={500}>Show:</Text>
              <Select
                value={pageSize}
                onChange={onPageSizeChange}
                data={[
                  { value: '25', label: '25' },
                  { value: '50', label: '50' },
                  { value: '100', label: '100' }
                ]}
                size="xs"
                style={{ width: 70 }}
                styles={{
                  input: {
                    fontWeight: 500,
                    minHeight: 28
                  }
                }}
              />
            </Group>
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
};
